import React, { useCallback, useMemo, useState } from 'react';
import { isBefore, isToday, startOfDay, startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { useDateFns } from '~/Hooks';
import { IEvent } from '~/Models/DTO/Event.model';
import { makeStyles } from '~/UIKit/Style';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';
import { WHITE } from '~/Constants/Colors.constants';
import {
  calculateEventPositionForDay,
  CalendarDay,
  CalendarDayHeader,
  CalendarHeaderContainer,
  CalendarViewContent,
  CreatingEvent,
  EditCalendarEventConfirmationModal,
  ICalculateEventPosition,
  UpdatingEvent,
} from '~/Screens/Calendar/CalendarView/components';
import { groupEvents } from '~/Screens/Calendar/CalendarView/utils';
import { ICalendarDayEvent, ICalendarOffset } from '~/Screens/Calendar/interfaces';
import {
  useUpdateCalendarEvent,
  useUpdateFullDayActivity,
} from '~/Screens/Calendar/CalendarView/hooks';
import { EVENT_OFFSET_TOP, TIME_TABLE_OFFSET_TOP } from '~/Screens/Calendar/constants';
import { CalendarSkeletonEvent } from '../components/CalendarSkeletonEvent';

export const useStyles = makeStyles(
  () => ({
    dayHeader: {
      fontSize: FONT_SIZE_LARGE,
      backgroundColor: WHITE,
      padding: '16px 0',
      top: '0',
      zIndex: 3,
    },
  }),
  { name: 'DailyView' },
);

interface IMonthlyView {
  selectedDay: Date;
  events: IEvent[][];
  from: string | null;
  to: string | null;
  isLoading?: boolean;
}

export const DailyView = ({ selectedDay, events, from, to, isLoading = false }: IMonthlyView) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [offset, setOffset] = useState<ICalendarOffset>({ width: 0, height: 0 });
  const [calendarDayClicked, setCalendarDayClicked] = useState<ICalendarDayEvent>(null);
  const [headerOffset, setHeaderOffset] = useState<ICalendarOffset>({ width: 0, height: 0 });
  const { formatDate } = useDateFns();
  const todayDate = startOfToday();
  const isPast = useMemo(() => isBefore(selectedDay, todayDate), [selectedDay, todayDate]);
  const weekdayLabel = selectedDay.toLocaleString('default', { weekday: 'long' });
  const dateLabel = formatDate(selectedDay.toISOString(), 'dd LLL yyyy');
  const getEventPosition = useCallback(
    ({ item, differenceFromInitialOffset }: Omit<ICalculateEventPosition, 'offset'>) =>
      calculateEventPositionForDay({ item, offset, differenceFromInitialOffset }),
    [offset],
  );
  const {
    updateEvent,
    updatingEvent,
    onCloseEditEvent,
    onConfirmEditEvent,
    confirmationOptions,
  } = useUpdateCalendarEvent({ offset, getEventPosition, from, to });
  const groupedEvent = useMemo(() => groupEvents({ events: events[0] }), [events[0]]);
  const hasToday = useMemo(() => isToday(selectedDay), [selectedDay]);
  const {
    updateFullDayActivity,
    updatingFullDayActivity,
    onCloseEditFullDayActivity,
    onConfirmEditFullDayActivity,
    confirmationOptionsForFullDayActivity,
  } = useUpdateFullDayActivity({
    getEventPosition,
  });

  return (
    <>
      <CalendarHeaderContainer
        offset={offset}
        headerOffset={headerOffset}
        setHeaderOffset={setHeaderOffset}
        getEventPosition={getEventPosition}
      >
        {offset.width && (
          <CalendarDayHeader
            width={offset.width}
            isPast={isPast}
            events={groupedEvent?.fullDayActivity}
            className={classes.dayHeader}
            updateFullDayActivity={updateFullDayActivity}
          >
            {hasToday && `${t('app.today')} `}
            <strong>
              {hasToday && '|'} {weekdayLabel}, {dateLabel}
            </strong>
          </CalendarDayHeader>
        )}
        <UpdatingEvent eventData={updatingFullDayActivity} top={headerOffset.top} />
        {confirmationOptionsForFullDayActivity && (
          <EditCalendarEventConfirmationModal
            eventName={updatingFullDayActivity?.event.name}
            onCloseEditEvent={onCloseEditFullDayActivity}
            onConfirmEditEvent={onConfirmEditFullDayActivity}
            confirmationOptions={confirmationOptionsForFullDayActivity}
          />
        )}
      </CalendarHeaderContainer>
      <CalendarViewContent
        offset={offset}
        hasToday={hasToday}
        setOffset={setOffset}
        getEventPosition={getEventPosition}
      >
        {isLoading && <CalendarSkeletonEvent />}
        {!isLoading && (
          <CalendarDay
            events={groupedEvent?.usualEvents}
            offset={offset}
            position="absolute"
            updateEvent={updateEvent}
            getEventPosition={getEventPosition}
            onCalendarDayClicked={setCalendarDayClicked}
          />
        )}
        <CreatingEvent
          top={TIME_TABLE_OFFSET_TOP + EVENT_OFFSET_TOP}
          offset={offset}
          startOfPeriod={startOfDay(selectedDay).toISOString()}
          contentPlacement="top"
          calendarDayEvent={calendarDayClicked}
        />
        <UpdatingEvent top={TIME_TABLE_OFFSET_TOP + EVENT_OFFSET_TOP} eventData={updatingEvent} />
        {confirmationOptions && (
          <EditCalendarEventConfirmationModal
            eventName={updatingEvent?.event.name}
            onCloseEditEvent={onCloseEditEvent}
            onConfirmEditEvent={onConfirmEditEvent}
            confirmationOptions={confirmationOptions}
          />
        )}
      </CalendarViewContent>
    </>
  );
};
